import React, { useState } from 'react';
import { Delete, Edit } from '@mui/icons-material';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { EventLocation } from '../../../../types';
import { eventsService } from '../../../../core/services/eventsService';
import styles from '../SchedulerSetting.module.scss';

interface EventLocationsListProps {
	eventLocations: EventLocation[];
	onEventLocationCreated: () => void;
	onEditClick: (location: EventLocation) => void;
	canEdit?: boolean;
	canDelete?: boolean;
}

const EventLocationsList: React.FC<EventLocationsListProps> = ({
	eventLocations,
	onEventLocationCreated,
	onEditClick,
	canEdit = false,
	canDelete = false,
}) => {
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [locationToDelete, setLocationToDelete] = useState<EventLocation | null>(null);

	const handleDeleteClick = (location: EventLocation) => {
		if (!canDelete || eventLocations.length <= 1) return;
		setLocationToDelete(location);
		setDeleteDialogOpen(true);
	};

	const handleConfirmDelete = async () => {
		if (!locationToDelete) return;
		
		try {
			await eventsService.deleteEventLocation(locationToDelete.id);
			setDeleteDialogOpen(false);
			setLocationToDelete(null);
			onEventLocationCreated();
		} catch (e) {
			console.error('Failed to delete event location:', e);
			setDeleteDialogOpen(false);
			setLocationToDelete(null);
		}
	};

	const handleCancelDelete = () => {
		setDeleteDialogOpen(false);
		setLocationToDelete(null);
	};

	return (
		<>
			<div className={styles.tableContainer}>
				<table className={styles.workplaceTable}>
					<thead>
						<tr>
							<th>Location</th>
							<th style={{ width: 48, textAlign: 'center' }}>Edit</th>
							<th style={{ width: 48, textAlign: 'center' }}>Delete</th>
						</tr>
					</thead>
					<tbody>
						{eventLocations.length === 0 ? (
							<tr>
								<td colSpan={3} style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
									No records found
								</td>
							</tr>
						) : (
							eventLocations.map((location) => (
								<tr key={location.id}>
									<td>
										<span>{location.label || location.name}</span>
									</td>
									<td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
										{(() => {
											const editDisabled = !canEdit;
											return (
										<button
											className={styles.iconButton}
											title={editDisabled ? "You do not have permission to edit" : "Edit"}
											onClick={() => !editDisabled && onEditClick(location)}
											disabled={editDisabled}
											style={{ opacity: editDisabled ? 0.5 : 1, cursor: editDisabled ? 'not-allowed' : 'pointer' }}
										>
											<Edit className={styles.editIcon} />
										</button>
											);
										})()}
									</td>
									<td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
										{(() => {
											const deleteDisabled = !canDelete || eventLocations.length <= 1;
											return (
										<button
											onClick={() => !deleteDisabled && handleDeleteClick(location)}
											title={
												deleteDisabled
													? !canDelete
														? "You do not have permission to delete"
														: "Cannot delete the last location"
													: "Delete Location"
											}
											className={styles.iconButton}
											disabled={deleteDisabled}
											style={{
												opacity: deleteDisabled ? 0.5 : 1,
												cursor: deleteDisabled ? 'not-allowed' : 'pointer',
											}}
										>
											<Delete className={styles.deleteIcon} />
										</button>
											);
										})()}
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>

			{/* Delete Confirmation Dialog */}
			<Dialog
				open={deleteDialogOpen}
				onClose={handleCancelDelete}
				aria-labelledby="delete-dialog-title"
				aria-describedby="delete-dialog-description"
				maxWidth="sm"
				fullWidth
				style={{ zIndex: 9999 }}
				PaperProps={{
					style: {
						backgroundColor: 'white',
						boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
						borderRadius: '8px',
						minWidth: '400px'
					}
				}}
			>
				<DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
				<DialogContent>
					<DialogContentText id="delete-dialog-description">
						Are you sure you want to delete the event location{' '}
						{locationToDelete && <strong>&ldquo;{locationToDelete.label || locationToDelete.name}&rdquo;</strong>}? This action cannot be undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleCancelDelete}
						style={{ color: 'black' }}
					>
						Cancel
					</Button>
					<Button
						style={{ backgroundColor: 'red', color: 'white' }}
						onClick={handleConfirmDelete}
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default EventLocationsList;

